/** @fileoverview Dialog that asks the viewer an action's confirm question before it runs. */

import { CircleHelp } from 'lucide-react';

import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

import type { FabrikActionConfirm } from '../../types/apps';
import { useThemeContainer } from './themeContext';

type ActionConfirmDialogProps = {
  /** The question of the action waiting to run, or undefined when none is waiting. */
  confirm?: FabrikActionConfirm;
  onAccept: () => void;
  onCancel: () => void;
};

/**
 * Shows the confirm question of an action that waits to run. It renders inside the app's themed
 * element, so its buttons and icon take the app's colour. Accepting runs the action; closing the
 * dialog or pressing Cancel drops the run.
 * @param props the question, and what accepting and cancelling do
 */
export default function ActionConfirmDialog({
  confirm,
  onAccept,
  onCancel,
}: ActionConfirmDialogProps) {
  const container = useThemeContainer();
  return (
    <Dialog open={Boolean(confirm)} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <DialogContent
        container={container ?? undefined}
        className="gap-0 overflow-hidden rounded-2xl p-0 shadow-xl sm:max-w-md"
      >
        <DialogHeader className="flex-row items-start gap-4 px-6 pt-6 pb-5">
          <span
            className="grid size-10 shrink-0 place-items-center rounded-full bg-primary/10 text-primary"
            aria-hidden="true"
          >
            <CircleHelp className="size-5" />
          </span>
          <div className="flex min-w-0 flex-col gap-1.5 pt-0.5 pr-6">
            <DialogTitle className="type-heading font-semibold tracking-tight text-balance">
              {confirm?.title}
            </DialogTitle>
            <DialogDescription className="leading-relaxed">{confirm?.message}</DialogDescription>
          </div>
        </DialogHeader>
        <DialogFooter className="rounded-none bg-muted/30 px-6 py-4">
          <Button type="button" variant="outline" size="lg" className="px-4" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="button" size="lg" className="px-4" onClick={onAccept}>
            {confirm?.button}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
